import React from "react";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { DocumentFolder } from "../models/Document";

interface ProcessingStatusProps {
    folder: DocumentFolder;
    onShowDetails: (folder: DocumentFolder) => void;
}

const ProcessingStatus: React.FC<ProcessingStatusProps> = ({ folder, onShowDetails }) => {
    const { t } = useTranslation(["documents"]);

    const { successCount, failureCount, skippedCount, totalCount, processingStatus } = folder;

    // Handle cases where processing data is not available
    if (processingStatus === undefined || totalCount === undefined) {
        return (
            <>
                <Badge
                    bg="secondary"
                    className="d-flex align-items-center gap-1 cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        onShowDetails(folder);
                    }}
                >
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {t("common:processingStates.RUNNING")}
                </Badge>
            </>
        );
    }

    // Calculate processed count and determine status
    const processedCount = (successCount || 0) + (failureCount || 0) + (skippedCount || 0);
    let badgeVariant: string;
    let statusText: string;
    let tooltipContent: string;

    switch (processingStatus) {
        case "FINISHED":
            badgeVariant = "success";
            statusText =
                skippedCount && skippedCount > 0
                    ? `${processedCount}/${totalCount} ✓ (${skippedCount} ${t("processingStatus.statusText.skipped")})`
                    : `${successCount}/${totalCount} ✓`;
            tooltipContent =
                skippedCount && skippedCount > 0
                    ? t("processingStatus.tooltips.finishedWithSkipped", {
                          totalCount,
                          successCount,
                          skippedCount,
                      })
                    : t("processingStatus.tooltips.finished", { count: totalCount });
            break;
        case "FAILED":
            badgeVariant = "danger";
            statusText = `0/${totalCount} ✗`;
            tooltipContent = t("processingStatus.tooltips.failed", { count: totalCount });
            break;
        case "COMPLETED_WITH_ERRORS":
            badgeVariant = "warning";
            statusText =
                skippedCount && skippedCount > 0
                    ? `${processedCount}/${totalCount} (${failureCount} ${t("processingStatus.statusText.errors")}, ${skippedCount} ${t("processingStatus.statusText.skipped")})`
                    : `${successCount}/${totalCount} ✓ (${failureCount} ${t("processingStatus.statusText.errors")})`;
            tooltipContent =
                skippedCount && skippedCount > 0
                    ? t("processingStatus.tooltips.completedWithErrorsAndSkipped", {
                          processedCount,
                          totalCount,
                          successCount,
                          failureCount,
                          skippedCount,
                      })
                    : t("processingStatus.tooltips.completedWithErrors", {
                          processedCount,
                          totalCount,
                          successCount,
                          failureCount,
                      });
            break;
        case "RUNNING":
            badgeVariant = "info";
            statusText = `${processedCount}/${totalCount} ${t("processingStatus.statusText.processing")}`;
            tooltipContent = t("processingStatus.tooltips.running", { processedCount, totalCount });
            break;
        default:
            badgeVariant = "secondary";
            statusText = t("processingStatus.statusText.unknown");
            tooltipContent = t("processingStatus.tooltips.unknown");
    }

    const tooltip = <Tooltip id={`processing-status-tooltip`}>{tooltipContent}</Tooltip>;

    const handleBadgeClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row click from firing
        onShowDetails(folder);
    };

    return (
        <>
            <OverlayTrigger placement="top" overlay={tooltip}>
                <Badge bg={badgeVariant} className="cursor-pointer" onClick={handleBadgeClick}>
                    {statusText}
                </Badge>
            </OverlayTrigger>
        </>
    );
};

export default ProcessingStatus;
